import React, { useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { CustomizableReportTable } from '../reports/CustomizableReportTable';
import { useAccountColumns } from './useAccountColumns';

const UNIT = ['Matched', 'Mismatch', 'Not in Portal', 'Not in BenPos'];
const CHECK = ['Match', 'Mismatch', 'N/A'];
const RECON_KEYS = {
  exceptions: ['pan', 'account_name', 'benpos_name', 'folio_no', 'isin', 'portal_units', 'benpos_units', 'difference', 'status', 'notes'],
  bank: ['pan', 'account_name', 'benpos_name', 'portal_bank_account', 'benpos_bank_account', 'bank_status', 'portal_ifsc', 'benpos_ifsc', 'ifsc_status'],
  full: ['pan', 'account_name', 'benpos_name', 'folio_no', 'isin', 'class_name', 'portal_units', 'benpos_units', 'difference', 'status', 'portal_bank_account', 'benpos_bank_account', 'bank_status', 'portal_ifsc', 'benpos_ifsc', 'ifsc_status', 'notes'],
};
const EMPTY = { unit: [], bank: [], ifsc: [], eitherMismatch: false, isin: '', nonZero: false };
const PRESETS = [
  { id: 'exceptions', label: 'Unit Exceptions', filters: { ...EMPTY, unit: ['Mismatch', 'Not in Portal', 'Not in BenPos'] } },
  { id: 'bank', label: 'Bank / IFSC Exceptions', filters: { ...EMPTY, eitherMismatch: true } },
  { id: 'full', label: 'Full Reconciliation', filters: EMPTY },
];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const Chip = ({ active, onClick, children, count, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white text-gray-600 hover:border-gray-400'}`}>
    {children}{count != null && <span className={`ml-1.5 ${active ? 'text-teal-100' : 'text-gray-400'}`}>{count}</span>}
  </button>
);

const ChipRow = ({ label, options, selected, onChange, counts, testPrefix }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-xs text-gray-500 w-28">{label}</span>
    {options.map((o) => (
      <Chip key={o} active={selected.includes(o)} count={counts[o] || 0} testId={`${testPrefix}-${slug(o)}`}
        onClick={() => onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o])}>{o}</Chip>
    ))}
    <span className="text-[11px] text-gray-400">{selected.length ? 'any of selected' : 'all'}</span>
  </div>
);

const countBy = (rows, key) => rows.reduce((a, r) => ({ ...a, [r[key]]: (a[r[key]] || 0) + 1 }), {});

const applyFilters = (rows, f) => rows.filter((r) => (!f.unit.length || f.unit.includes(r.status))
  && (!f.bank.length || f.bank.includes(r.bank_status))
  && (!f.ifsc.length || f.ifsc.includes(r.ifsc_status))
  && (!f.eitherMismatch || r.bank_status === 'Mismatch' || r.ifsc_status === 'Mismatch')
  && (!f.isin || r.isin === f.isin)
  && (!f.nonZero || Math.abs(r.difference) > 0.0001));

const describe = (f) => [
  f.unit.length && `Unit Status: ${f.unit.join(' / ')}`,
  f.bank.length && `Bank A/c Check: ${f.bank.join(' / ')}`,
  f.ifsc.length && `IFSC Check: ${f.ifsc.join(' / ')}`,
  f.eitherMismatch && 'Bank or IFSC mismatch',
  f.isin && `ISIN ${f.isin}`,
  f.nonZero && 'Non-zero difference',
].filter(Boolean);

export const DematReportsTab = ({ recon }) => {
  const [preset, setPreset] = useState('exceptions');
  const [f, setF] = useState(PRESETS[0].filters);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const isins = useMemo(() => [...new Set(recon.rows.map((r) => r.isin).filter(Boolean))], [recon.rows]);
  const counts = useMemo(() => ({ unit: countBy(recon.rows, 'status'), bank: countBy(recon.rows, 'bank_status'), ifsc: countBy(recon.rows, 'ifsc_status') }), [recon.rows]);
  const filtered = useMemo(() => applyFilters(recon.rows, f), [recon.rows, f]);
  const { columns, rows, onVisibleChange } = useAccountColumns(recon, filtered);
  const active = describe(f);

  return (
    <div data-testid="demat-reports-tab">
      <div className="bg-white border rounded-lg p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 w-28">Report</span>
          {PRESETS.map((x) => <Chip key={x.id} active={preset === x.id} onClick={() => { setPreset(x.id); setF(x.filters); }} testId={`demat-preset-${x.id}`}>{x.label}</Chip>)}
        </div>
        <ChipRow label="Unit Status" options={UNIT} selected={f.unit} onChange={(unit) => set({ unit })} counts={counts.unit} testPrefix="demat-status" />
        <ChipRow label="Bank A/c Check" options={CHECK} selected={f.bank} onChange={(bank) => set({ bank })} counts={counts.bank} testPrefix="demat-bank" />
        <ChipRow label="IFSC Check" options={CHECK} selected={f.ifsc} onChange={(ifsc) => set({ ifsc })} counts={counts.ifsc} testPrefix="demat-ifsc" />
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-xs text-gray-500 w-28">Other</span>
          <select value={f.isin} onChange={(e) => set({ isin: e.target.value })} className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="demat-isin-filter">
            <option value="">All ISINs</option>
            {isins.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={f.eitherMismatch} onChange={(e) => set({ eitherMismatch: e.target.checked })} data-testid="demat-bank-only" /> Bank <b>or</b> IFSC mismatch</label>
          <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={f.nonZero} onChange={(e) => set({ nonZero: e.target.checked })} data-testid="demat-nonzero-only" /> Non-zero difference only</label>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t text-xs text-gray-600" data-testid="demat-active-filters">
          <span>Showing <b>{filtered.length}</b> of {recon.rows.length} rows</span>
          {active.length > 0 && <span className="text-gray-400">· matching ALL of: {active.join('  AND  ')}</span>}
          {active.length > 0 && (
            <button onClick={() => setF(EMPTY)} className="ml-auto inline-flex items-center gap-1 text-teal-700 hover:underline" data-testid="demat-reset-filters">
              <RotateCcw size={12} /> Reset filters
            </button>
          )}
        </div>
      </div>
      {!columns ? <div className="p-8 text-center text-gray-500">Loading columns…</div> : (
        <CustomizableReportTable
          key={preset} columns={columns} rows={rows} defaultVisible={RECON_KEYS[preset]} storageKey={`report-cols-demat-v2-${preset}`}
          fileName={`benpos_${preset}_${recon.upload.as_on_date}`} testId="demat-report" rowKey={(r, i) => `${r.pan}-${r.isin}-${i}`}
          onVisibleChange={onVisibleChange}
        />
      )}
    </div>
  );
};
